import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Loader2,
  Shield,
  FileText,
  Eye,
  EyeOff,
  RefreshCw,
  CheckCircle,
  ClipboardCheck,
  Users,
  Briefcase,
  AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLegal = () => {
  const [privacyPolicy, setPrivacyPolicy] = useState({ title: '', content: '', is_active: true });
  const [termsOfService, setTermsOfService] = useState({ title: '', content: '', is_active: true });
  const [clientConsentForm, setClientConsentForm] = useState({ title: '', content: '', is_active: true });
  const [partnerConsentForm, setPartnerConsentForm] = useState({ title: '', content: '', is_active: true });
  const [disclaimerText, setDisclaimerText] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [savingTerms, setSavingTerms] = useState(false);
  const [savingClientConsent, setSavingClientConsent] = useState(false);
  const [savingPartnerConsent, setSavingPartnerConsent] = useState(false);
  const [savingDisclaimer, setSavingDisclaimer] = useState(false);
  const [activeTab, setActiveTab] = useState('privacy');

  const getAuthHeader = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchLegalPages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/legal`, {
        headers: getAuthHeader()
      });
      
      if (response.data.privacy_policy) {
        setPrivacyPolicy({
          title: response.data.privacy_policy.title || 'Privacy Policy',
          content: response.data.privacy_policy.content || '',
          is_active: response.data.privacy_policy.is_active ?? true
        });
      }
      
      if (response.data.terms_of_service) {
        setTermsOfService({
          title: response.data.terms_of_service.title || 'Terms of Service',
          content: response.data.terms_of_service.content || '',
          is_active: response.data.terms_of_service.is_active ?? true
        });
      }
      
      if (response.data.client_consent_form) {
        setClientConsentForm({
          title: response.data.client_consent_form.title || 'Client Consent Form',
          content: response.data.client_consent_form.content || '',
          is_active: response.data.client_consent_form.is_active ?? true
        });
      }
      
      if (response.data.partner_consent_form) {
        setPartnerConsentForm({
          title: response.data.partner_consent_form.title || 'Partner Consent Form',
          content: response.data.partner_consent_form.content || '',
          is_active: response.data.partner_consent_form.is_active ?? true
        });
      }
    } catch (error) {
      console.error('Failed to fetch legal pages:', error);
      toast.error('Failed to load legal pages');
    } finally {
      setLoading(false);
    }

    // Also fetch the pre-payment disclaimer (separate endpoint)
    try {
      const disc = await axios.get(`${API}/admin/legals/disclaimer`, {
        headers: getAuthHeader()
      });
      setDisclaimerText(disc.data?.disclaimer_text || '');
    } catch (e) {
      console.error('Failed to fetch disclaimer:', e);
    }
  };

  useEffect(() => {
    fetchLegalPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSavePrivacy = async () => {
    if (!privacyPolicy.title.trim() || !privacyPolicy.content.trim()) {
      toast.error('Please fill in both title and content');
      return;
    }

    setSavingPrivacy(true);
    try {
      await axios.put(`${API}/admin/legal/privacy_policy`, privacyPolicy, {
        headers: getAuthHeader()
      });
      toast.success('Privacy Policy saved successfully');
    } catch (error) {
      console.error('Failed to save Privacy Policy:', error);
      toast.error('Failed to save Privacy Policy');
    } finally {
      setSavingPrivacy(false);
    }
  };

  const handleSaveTerms = async () => {
    if (!termsOfService.title.trim() || !termsOfService.content.trim()) {
      toast.error('Please fill in both title and content');
      return;
    }

    setSavingTerms(true);
    try {
      await axios.put(`${API}/admin/legal/terms_of_service`, termsOfService, {
        headers: getAuthHeader()
      });
      toast.success('Terms of Service saved successfully');
    } catch (error) {
      console.error('Failed to save Terms of Service:', error);
      toast.error('Failed to save Terms of Service');
    } finally {
      setSavingTerms(false);
    }
  };

  const handleSaveClientConsent = async () => {
    if (!clientConsentForm.title.trim() || !clientConsentForm.content.trim()) {
      toast.error('Please fill in both title and content');
      return;
    }

    setSavingClientConsent(true);
    try {
      await axios.put(`${API}/admin/legal/client_consent_form`, clientConsentForm, {
        headers: getAuthHeader()
      });
      toast.success('Client Consent Form saved successfully');
    } catch (error) {
      console.error('Failed to save Client Consent Form:', error);
      toast.error('Failed to save Client Consent Form');
    } finally {
      setSavingClientConsent(false);
    }
  };

  const handleSavePartnerConsent = async () => {
    if (!partnerConsentForm.title.trim() || !partnerConsentForm.content.trim()) {
      toast.error('Please fill in both title and content');
      return;
    }

    setSavingPartnerConsent(true);
    try {
      await axios.put(`${API}/admin/legal/partner_consent_form`, partnerConsentForm, {
        headers: getAuthHeader()
      });
      toast.success('Partner Consent Form saved successfully');
    } catch (error) {
      console.error('Failed to save Partner Consent Form:', error);
      toast.error('Failed to save Partner Consent Form');
    } finally {
      setSavingPartnerConsent(false);
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('This will reset to default legal pages. Continue?')) {
      return;
    }

    try {
      await axios.post(`${API}/admin/legal/seed-defaults`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Default legal pages loaded');
      fetchLegalPages();
    } catch (error) {
      console.error('Failed to seed defaults:', error);
      toast.error(error.response?.data?.detail || 'Failed to load defaults');
    }
  };

  const handleSaveDisclaimer = async () => {
    if (!disclaimerText.trim()) {
      toast.error('Disclaimer text cannot be empty');
      return;
    }
    setSavingDisclaimer(true);
    try {
      await axios.put(`${API}/admin/legals/disclaimer`, {
        disclaimer_text: disclaimerText
      }, { headers: getAuthHeader() });
      toast.success('Pre-payment disclaimer saved');
    } catch (error) {
      console.error('Failed to save disclaimer:', error);
      toast.error(error?.response?.data?.detail || 'Failed to save disclaimer');
    } finally {
      setSavingDisclaimer(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-legal-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Legal Pages</h1>
          <p className="text-slate-400 text-sm mt-1">Manage Privacy Policy, Terms of Service, and Consent Forms</p>
        </div>
        <Button
          onClick={handleSeedDefaults}
          variant="outline"
          className="border-slate-600 text-slate-300 hover:bg-slate-800"
          data-testid="seed-defaults-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Reset to Defaults
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-800 border border-slate-700 p-1 flex-wrap h-auto gap-1">
          <TabsTrigger 
            value="privacy" 
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 text-slate-300"
            data-testid="privacy-tab"
          >
            <Shield className="w-4 h-4 mr-2" />
            Privacy Policy
          </TabsTrigger>
          <TabsTrigger 
            value="terms" 
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 text-slate-300"
            data-testid="terms-tab"
          >
            <FileText className="w-4 h-4 mr-2" />
            Terms of Service
          </TabsTrigger>
          <TabsTrigger 
            value="client-consent" 
            className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-900 text-slate-300"
            data-testid="client-consent-tab"
          >
            <Users className="w-4 h-4 mr-2" />
            Client Consent
          </TabsTrigger>
          <TabsTrigger 
            value="partner-consent" 
            className="data-[state=active]:bg-blue-500 data-[state=active]:text-slate-900 text-slate-300"
            data-testid="partner-consent-tab"
          >
            <Briefcase className="w-4 h-4 mr-2" />
            Partner Consent
          </TabsTrigger>
          <TabsTrigger 
            value="disclaimer" 
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 text-slate-300"
            data-testid="disclaimer-tab"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Pre-Payment Disclaimer
          </TabsTrigger>
        </TabsList>

        {/* Privacy Policy Tab */}
        <TabsContent value="privacy" className="mt-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-400" />
                  Privacy Policy
                </CardTitle>
                <div className="flex items-center gap-2">
                  {privacyPolicy.is_active ? (
                    <span className="flex items-center gap-1 text-green-400 text-sm">
                      <Eye className="w-4 h-4" /> Visible
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-slate-500 text-sm">
                      <EyeOff className="w-4 h-4" /> Hidden
                    </span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Page Title</label>
                <Input
                  value={privacyPolicy.title}
                  onChange={(e) => setPrivacyPolicy({ ...privacyPolicy, title: e.target.value })}
                  placeholder="Privacy Policy"
                  className="bg-slate-900 border-slate-600 text-white"
                  data-testid="privacy-title-input"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">
                  Content <span className="text-slate-500">(Supports markdown: ## for headings, ### for subheadings, **bold**, - for lists)</span>
                </label>
                <Textarea
                  value={privacyPolicy.content}
                  onChange={(e) => setPrivacyPolicy({ ...privacyPolicy, content: e.target.value })}
                  placeholder="Enter privacy policy content..."
                  rows={20}
                  className="bg-slate-900 border-slate-600 text-white font-mono text-sm"
                  data-testid="privacy-content-textarea"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={privacyPolicy.is_active}
                  onCheckedChange={(checked) => setPrivacyPolicy({ ...privacyPolicy, is_active: checked })}
                  data-testid="privacy-active-switch"
                />
                <span className="text-sm text-slate-300">Show on website</span>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleSavePrivacy}
                  disabled={savingPrivacy}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                  data-testid="save-privacy-btn"
                >
                  {savingPrivacy ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Privacy Policy
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Terms of Service Tab */}
        <TabsContent value="terms" className="mt-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-400" />
                  Terms of Service
                </CardTitle>
                <div className="flex items-center gap-2">
                  {termsOfService.is_active ? (
                    <span className="flex items-center gap-1 text-green-400 text-sm">
                      <Eye className="w-4 h-4" /> Visible
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-slate-500 text-sm">
                      <EyeOff className="w-4 h-4" /> Hidden
                    </span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Page Title</label>
                <Input
                  value={termsOfService.title}
                  onChange={(e) => setTermsOfService({ ...termsOfService, title: e.target.value })}
                  placeholder="Terms of Service"
                  className="bg-slate-900 border-slate-600 text-white"
                  data-testid="terms-title-input"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">
                  Content <span className="text-slate-500">(Supports markdown: ## for headings, ### for subheadings, **bold**, - for lists)</span>
                </label>
                <Textarea
                  value={termsOfService.content}
                  onChange={(e) => setTermsOfService({ ...termsOfService, content: e.target.value })}
                  placeholder="Enter terms of service content..."
                  rows={20}
                  className="bg-slate-900 border-slate-600 text-white font-mono text-sm"
                  data-testid="terms-content-textarea"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={termsOfService.is_active}
                  onCheckedChange={(checked) => setTermsOfService({ ...termsOfService, is_active: checked })}
                  data-testid="terms-active-switch"
                />
                <span className="text-sm text-slate-300">Show on website</span>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleSaveTerms}
                  disabled={savingTerms}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                  data-testid="save-terms-btn"
                >
                  {savingTerms ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Terms of Service
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Client Consent Form Tab */}
        <TabsContent value="client-consent" className="mt-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-400" />
                  Client Consent Form
                </CardTitle>
                <div className="flex items-center gap-2">
                  {clientConsentForm.is_active ? (
                    <span className="flex items-center gap-1 text-green-400 text-sm">
                      <Eye className="w-4 h-4" /> Active
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-slate-500 text-sm">
                      <EyeOff className="w-4 h-4" /> Inactive
                    </span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 mb-4">
                <p className="text-emerald-300 text-sm">
                  <strong>Note:</strong> This consent form will be shown to <strong>Clients</strong> after their first login. 
                  They must accept it before accessing the system. Consent logs are recorded with timestamp, IP address, and user ID.
                </p>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Form Title</label>
                <Input
                  value={clientConsentForm.title}
                  onChange={(e) => setClientConsentForm({ ...clientConsentForm, title: e.target.value })}
                  placeholder="Client Data Processing Consent"
                  className="bg-slate-900 border-slate-600 text-white"
                  data-testid="client-consent-title-input"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">
                  Content <span className="text-slate-500">(Supports markdown: ## for headings, ### for subheadings, **bold**, - for lists)</span>
                </label>
                <Textarea
                  value={clientConsentForm.content}
                  onChange={(e) => setClientConsentForm({ ...clientConsentForm, content: e.target.value })}
                  placeholder="Enter client consent form content..."
                  rows={20}
                  className="bg-slate-900 border-slate-600 text-white font-mono text-sm"
                  data-testid="client-consent-content-textarea"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={clientConsentForm.is_active}
                  onCheckedChange={(checked) => setClientConsentForm({ ...clientConsentForm, is_active: checked })}
                  data-testid="client-consent-active-switch"
                />
                <span className="text-sm text-slate-300">Require consent from clients</span>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleSaveClientConsent}
                  disabled={savingClientConsent}
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-900"
                  data-testid="save-client-consent-btn"
                >
                  {savingClientConsent ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Client Consent Form
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Partner Consent Form Tab */}
        <TabsContent value="partner-consent" className="mt-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-blue-400" />
                  Partner Consent Form
                </CardTitle>
                <div className="flex items-center gap-2">
                  {partnerConsentForm.is_active ? (
                    <span className="flex items-center gap-1 text-green-400 text-sm">
                      <Eye className="w-4 h-4" /> Active
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-slate-500 text-sm">
                      <EyeOff className="w-4 h-4" /> Inactive
                    </span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-4">
                <p className="text-blue-300 text-sm">
                  <strong>Note:</strong> This consent form will be shown to <strong>Partners</strong> after their first login. 
                  They must accept it before accessing the system. Consent logs are recorded with timestamp, IP address, and user ID.
                </p>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Form Title</label>
                <Input
                  value={partnerConsentForm.title}
                  onChange={(e) => setPartnerConsentForm({ ...partnerConsentForm, title: e.target.value })}
                  placeholder="Partner Data Processing Consent"
                  className="bg-slate-900 border-slate-600 text-white"
                  data-testid="partner-consent-title-input"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">
                  Content <span className="text-slate-500">(Supports markdown: ## for headings, ### for subheadings, **bold**, - for lists)</span>
                </label>
                <Textarea
                  value={partnerConsentForm.content}
                  onChange={(e) => setPartnerConsentForm({ ...partnerConsentForm, content: e.target.value })}
                  placeholder="Enter partner consent form content..."
                  rows={20}
                  className="bg-slate-900 border-slate-600 text-white font-mono text-sm"
                  data-testid="partner-consent-content-textarea"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={partnerConsentForm.is_active}
                  onCheckedChange={(checked) => setPartnerConsentForm({ ...partnerConsentForm, is_active: checked })}
                  data-testid="partner-consent-active-switch"
                />
                <span className="text-sm text-slate-300">Require consent from partners</span>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleSavePartnerConsent}
                  disabled={savingPartnerConsent}
                  className="bg-blue-500 hover:bg-blue-400 text-slate-900"
                  data-testid="save-partner-consent-btn"
                >
                  {savingPartnerConsent ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Partner Consent Form
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pre-Payment Disclaimer Tab */}
        <TabsContent value="disclaimer" className="mt-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                Pre-Payment Disclaimer
              </CardTitle>
              <p className="text-xs text-slate-400 mt-1">
                Shown in a modal to clients after Step 8 of the Will flow, just before they
                hit "Pay & Generate". Use plain text with line breaks, or markdown-style bullets
                (<code className="bg-slate-700 px-1 rounded">•</code>).
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Disclaimer Text</label>
                <textarea
                  value={disclaimerText}
                  onChange={(e) => setDisclaimerText(e.target.value)}
                  placeholder={`By proceeding with payment, you acknowledge that:\n\n• You have reviewed all the information provided in your will document\n• The details entered are accurate and complete to the best of your knowledge\n• ...`}
                  rows={12}
                  className="w-full p-3 bg-slate-900 border border-slate-600 rounded-lg text-white font-mono text-sm resize-y"
                  data-testid="disclaimer-textarea"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {disclaimerText.length} characters. Leave blank to fall back to the built-in default.
                </p>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleSaveDisclaimer}
                  disabled={savingDisclaimer}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                  data-testid="save-disclaimer-btn"
                >
                  {savingDisclaimer ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Disclaimer
                </Button>
              </div>

              {/* Live preview */}
              {disclaimerText.trim() && (
                <div className="mt-6">
                  <p className="text-sm text-slate-400 mb-2">Live preview (how clients will see it):</p>
                  <div className="bg-slate-900 border border-amber-500/30 rounded-lg p-4">
                    <div className="text-slate-300 text-sm whitespace-pre-line">
                      {disclaimerText}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Help Text */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-sm text-slate-300 font-medium">Markdown Formatting Guide</p>
              <ul className="text-xs text-slate-400 mt-2 space-y-1">
                <li><code className="bg-slate-700 px-1 rounded">## Heading</code> - Main section heading</li>
                <li><code className="bg-slate-700 px-1 rounded">### Subheading</code> - Subsection heading</li>
                <li><code className="bg-slate-700 px-1 rounded">**bold text**</code> - Bold text</li>
                <li><code className="bg-slate-700 px-1 rounded">- item</code> - List item</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLegal;
